/**
 * Foundation marketing brand tokens — the single source of truth.
 *
 * Direction: "Title Plan", warmed.
 *
 * The system is the Land Registry title plan — paper, ink, grid crosses, and
 * the red boundary line that marks exactly what you own. The warmth comes from
 * the outgoing quiet-luxury identity: a laid-paper ground rather than a cool
 * drafting grey, a warm near-black rather than a blue-black, and one bronze
 * accent held back for the human moments (testimonials, the founding-member
 * offer, the price). Red draws the lines; bronze carries the warmth.
 *
 * WHY THIS FILE EXISTS
 * The old palette was a seven-line block of JS constants copy-pasted into
 * roughly sixteen page files, so every colour change meant sixteen edits and
 * the pages had already drifted apart from one another. Import from here.
 * Nothing under `src/pages/Foundation*` or `src/components/Foundation*` should
 * declare a brand hex of its own.
 *
 * SCOPE WARNING
 * These tokens are the *marketing* brand only. The shadcn tokens in index.css
 * (--background, --primary, --accent, …) drive the CRM and every white-label
 * tenant site; `lib/brand.jsx` repaints --accent at runtime from the tenant
 * record. Do not wire marketing colour through those variables — a tenant with
 * an amber brand would repaint the Foundation site.
 */

/* ---------------------------------------------------------------- palette */

export const C = {
  /* Statehouse "Clear" (1 Nov 2026). The keys keep their old names so every
     page that imports them follows without an edit; read them as roles.
     Sign-in, sign-up, password and payment pages all draw from here. */
  /** Page ground. */
  paper: "#FFFFFF",
  /** Alternating bands, notices, table header rows. */
  wash: "#F1F3F6",
  /** Cards and inputs: a step off the white page. */
  surface: "#F7F8FA",
  /** All display and body text. */
  ink: "#0B0D12",
  /** Subcopy, captions and labels. 8.1:1 on white. */
  secondary: "#4A5060",
  /** Lines and active underlines: the accent blue. */
  red: "#2452E8",
  /** The accent on the dark panel. */
  signal: "#9DB4FF",
  /** The accent as text and links: 6.2:1 on white. */
  bronze: "#2452E8",
  /** The accent on the dark panel only. */
  bronzeLight: "#9DB4FF",
  /** Every rule and border. Never text. */
  hairline: "#E6E8EE",
  /** The one dark surface. */
  panel: "#0B0D12",
  /** Body copy on the dark panel. */
  mutedDark: "#B4BAC6",
  hairlineDark: "rgba(255, 255, 255, 0.12)"
};

/**
 * Verified with WCAG relative luminance. Body text pairings clear 4.5:1;
 * `hairline` is the only token that fails and it is never permitted as text.
 *
 *   ink / paper            15.29    secondary / paper       6.44
 *   ink / wash             14.08    secondary / wash        5.93
 *   ink / surface          16.82    secondary / surface     7.08
 *   red / paper             5.46    bronze / paper          5.45
 *   red / wash              5.03    bronze / wash           5.02
 *   red / surface           6.00    bronze / surface        5.99
 *   paper / panel          15.75    signal / panel          5.86
 *   mutedDark / panel      10.97    bronzeLight / panel      7.16
 *
 * Re-run scripts/contrast-audit.mjs after any palette change.
 */

/* -------------------------------------------------------------- typography */

/**
 * Archivo for everything spoken, IBM Plex Mono for everything annotated.
 *
 * The expanded cut (width 115%) is display-only, 28px and up — it gives the
 * signage voice without a second family, but it hurts legibility at body size.
 *
 * These faces are loaded and applied under the `.fdn` scope in index.css. They
 * are NOT wired into the global `body` / `h1–h4` rules: those are inherited by
 * the CRM and every tenant public site, so re-pointing them re-types the whole
 * product.
 */
export const T = {
  display: "'Inter Tight', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', monospace",
  displayStyle: {
    fontFamily: "'Inter Tight', 'Inter', -apple-system, sans-serif",
    fontWeight: 700,
    letterSpacing: "-0.035em",
    lineHeight: 1.05
  },
  titleStyle: {
    fontFamily: "'Inter Tight', 'Inter', -apple-system, sans-serif",
    fontWeight: 600,
    letterSpacing: "-0.02em",
    lineHeight: 1.15
  },
  /** Labels: plain sentence case, lightly weighted. */
  labelStyle: {
    fontFamily: "'Inter', -apple-system, sans-serif",
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: "0",
    textTransform: "none"
  }
};

/** A label at an explicit size. */
export function label(size = 11, weight = 500) {
  // Clear (1 Nov 2026): labels read as words, not spaced capitals, and never
  // smaller than 13px. `size` and `weight` are kept for the callers.
  return {
    fontFamily: T.body,
    fontWeight: Math.max(weight, 600),
    fontSize: Math.max(size + 2, 13),
    letterSpacing: "0",
    textTransform: "none"
  };
}

/* ------------------------------------------------------------------ layout */

export const L = {
  maxWidth: 1200,
  /** Drawing-sheet rhythm. Sections are generous and evenly spaced. */
  section: {
    paddingTop: 128,
    paddingBottom: 128
  },
  sectionSm: {
    paddingTop: 88,
    paddingBottom: 88
  },
  radius: 10,
  /**
   * One shadow exists on the entire site: under the hero screenshot. Everything
   * else earns its depth from tone steps and hairlines.
   */
  heroShadow: "0 24px 48px -12px rgba(26, 25, 23, 0.06)"
};

/* ------------------------------------------------------------------ motion */

/**
 * Surveyor's restraint — lines are drawn, content is placed. Nothing loops,
 * nothing parallaxes, nothing moves after it settles.
 */
export const M = {
  ease: "cubic-bezier(0.2, 0.8, 0.2, 1)",
  /** Red boundary devices self-draw, once, on first view. */
  draw: "900ms",
  /** Content blocks fade up 12px. */
  enter: "400ms",
  /** Underlines grow, button bars sweep, arrows shift. */
  hover: "150ms"
};

/* ------------------------------------------------------------------ legacy */

/**
 * COMPATIBILITY SHIMS.
 *
 * The outgoing identity was a block of constants copy-pasted into sixteen
 * pages. Each page can now delete its block and import these instead, which
 * re-points its whole palette and typeface in one line — so the site never
 * sits half-rebranded while the pages are hand-set one at a time.
 *
 * A page is "converted" when it imports these. It is "hand-set" when it uses
 * the `C` tokens and the Brand primitives directly. Converted pages are on
 * palette and on typeface but still lay out to the old rhythm; work through
 * them in order of traffic.
 *
 * @deprecated Use the `C` tokens and components/foundation/Brand.
 */
export const INK = C.ink;
export const INK_SOFT = C.secondary;
/**
 * Bronze, not red. The outgoing COPPER was the everywhere-accent — overlines,
 * rules, numerals, links — and red cannot take that load: the moment it stops
 * being rationed it reads as error state rather than as the boundary line.
 * Bronze absorbs the volume; red is applied deliberately, per page, as pages
 * get hand-set.
 */
export const COPPER = C.bronze;
export const COPPER_SOFT = C.bronzeLight;
/** Bronze at card-tint strength, for the pale accent grounds some pages use. */
export const COPPER_LT = "#EEF2FF";
export const CREAM = C.paper;
export const PARCHMENT = C.wash;
export const MUTED = C.secondary;
/**
 * Replaces the half-dozen `hsla(158,36%,12%,0.0x)` borders the pages each spelled
 * out at slightly different opacities. One rule tone everywhere.
 */
export const HAIRLINE = C.hairline;

/**
 * The old inline serif style object. Family and tracking only — deliberately
 * no `fontWeight`, because call sites spread this and then set their own
 * weight, and forcing one here would turn every paragraph that uses it
 * semibold.
 *
 * @deprecated Use `T.displayStyle` / `T.titleStyle`, or the `.fdn-display`
 * class, which carry the correct weight and the expanded display width.
 */
export const serif = {
  fontFamily: T.display,
  letterSpacing: "-0.012em"
};
export default {
  C,
  T,
  L,
  M,
  label
};
